import { dayjs, APP_TIMEZONE } from '@/dayjs';
const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const wallClockFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIMEZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
});
const toMillis = (input) => Math.round(typeof input === 'number'
    ? input
    : input.valueOf());
const wallClockAt = (ms) => {
    const parts = {};
    for (const part of wallClockFormatter.formatToParts(new Date(ms))) {
        if (part.type !== 'literal') {
            parts[part.type] = Number(part.value);
        }
    }
    return {
        year: parts.year,
        month: parts.month,
        day: parts.day,
        hour: parts.hour,
        minute: parts.minute,
        second: parts.second,
        millisecond: ((ms % 1000) + 1000) % 1000
    };
};
const wallClockAsUtcMillis = (wall) => Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second, wall.millisecond);
/** UTC offset of APP_TIMEZONE at the given instant, in minutes. */
const offsetMinutesAt = (ms) => Math.round((wallClockAsUtcMillis(wallClockAt(ms)) - ms) / MINUTE_MS);
/**
 * The same instant, as a dayjs value with the correct APP_TIMEZONE offset and labels.
 *
 * Deliberately not tagged with a timezone the way `.tz()` does: for tagged values the
 * timezone plugin reroutes isBefore/isAfter/startOf through a wall-clock re-parse that
 * picks the wrong offset near a DST switch when the browser is outside Stockholm.
 */
export const toAppTime = (input) => {
    const ms = toMillis(input);
    return dayjs.utc(ms).utcOffset(offsetMinutesAt(ms));
};
/** Calendar date and clock time in APP_TIMEZONE. */
export const appWallClock = (input) => wallClockAt(toMillis(input));
/**
 * The instant at which APP_TIMEZONE clocks show the given wall-clock time.
 *
 * Out-of-range fields roll over (day 32 → next month). On the autumn switch the
 * repeated hour resolves to its first occurrence; in the spring gap the time is
 * moved forward by the gap (02:30 → 03:30), matching `dayjs.tz(string)`.
 */
export const fromAppWallClock = (wall) => {
    const asUtc = wallClockAsUtcMillis({ hour: 0, minute: 0, second: 0, millisecond: 0, ...wall });
    const offsetBefore = offsetMinutesAt(asUtc - DAY_MS);
    const offsetAfter = offsetMinutesAt(asUtc + DAY_MS);
    const candidates = (offsetBefore === offsetAfter ? [offsetBefore] : [offsetBefore, offsetAfter])
        .map(offset => asUtc - offset * MINUTE_MS)
        .filter(ms => wallClockAsUtcMillis(wallClockAt(ms)) === asUtc)
        .sort((a, b) => a - b);
    return toAppTime(candidates[0] ?? asUtc - offsetBefore * MINUTE_MS);
};
/** Elapsed-time arithmetic: exactly `hours` × 60 minutes later, whatever the clocks do. */
export const addAppHours = (input, hours) => toAppTime(toMillis(input) + hours * HOUR_MS);
/** Elapsed-time arithmetic: exactly `minutes` later, whatever the clocks do. */
export const addAppMinutes = (input, minutes) => toAppTime(toMillis(input) + minutes * MINUTE_MS);
/** Calendar arithmetic: the same wall-clock time `days` calendar days later (a day may be 23 or 25 hours). */
export const addAppDays = (input, days) => {
    const wall = appWallClock(input);
    return fromAppWallClock({ ...wall, day: wall.day + days });
};
/** Midnight at the start of the input's APP_TIMEZONE calendar day. */
export const startOfAppDay = (input) => {
    const { year, month, day } = appWallClock(input);
    return fromAppWallClock({ year, month, day });
};
/** Start of the input's clock hour. In the repeated autumn hour this stays in the same occurrence. */
export const startOfAppHour = (input) => {
    const ms = toMillis(input);
    const { minute, second, millisecond } = wallClockAt(ms);
    return toAppTime(ms - minute * MINUTE_MS - second * 1000 - millisecond);
};
/** The last millisecond of the input's APP_TIMEZONE calendar day. */
export const endOfAppDay = (input) => toAppTime(addAppDays(startOfAppDay(input), 1).valueOf() - 1);
/** The given clock time on the input's APP_TIMEZONE calendar day. */
export const atAppTime = (input, hour, minute = 0) => {
    const { year, month, day } = appWallClock(input);
    return fromAppWallClock({ year, month, day, hour, minute });
};
/** Midnight starting a 'YYYY-MM-DD' calendar date in APP_TIMEZONE. */
export const parseAppDate = (date) => {
    const [year, month, day] = date.split('-').map(Number);
    return fromAppWallClock({ year: year, month: month, day: day });
};
/** A 'YYYY-MM-DD' date and 'HH:mm' clock time in APP_TIMEZONE, as typed into a form. */
export const parseAppDateTime = (date, time) => {
    const [hour, minute] = time.split(':').map(Number);
    return atAppTime(parseAppDate(date), hour ?? 0, minute ?? 0);
};
/** 'YYYY-MM-DD' of the input's APP_TIMEZONE calendar day. */
export const formatAppDate = (input) => toAppTime(input).format('YYYY-MM-DD');
/** Whether both inputs fall on the same APP_TIMEZONE calendar day. */
export const isSameAppDay = (a, b) => formatAppDate(a) === formatAppDate(b);
/** Format the input as APP_TIMEZONE clocks show it. Safe for the repeated autumn hour. */
export const formatAppTime = (input, template) => toAppTime(input).format(template);
/** 'D MMM HH:mm – HH:mm', with the end date repeated only when the interval spans days. */
export const formatAppInterval = (start, end) => {
    const endFormatted = isSameAppDay(start, end) ? formatAppTime(end, 'HH:mm') : formatAppTime(end, 'D MMM HH:mm');
    return `${formatAppTime(start, 'D MMM HH:mm')} – ${endFormatted}`;
};
